import * as React from 'react';
import {omit} from 'lodash';
import {styled, emotionStyled} from '@packages/utilities/styled';
import useHitarea from '@packages/systems/ui/hooks/useHitarea';
import {visuallyHiddenStyles} from '@packages/systems/spring';
import {buttonStyle, buttonVariants} from '../ButtonShell';
import {Icon} from '../Icon';
import {
  type ButtonGroupItemProps,
  type ButtonGroupSizing,
} from './ButtonGroup.types';

const getLineHeight = (sizing: ButtonGroupSizing): string => {
  switch (sizing) {
    case 'small':
      return '16px';
    case 'medium':
      return '16px';
    case 'large':
      return '24px';
    default:
      sizing as never;
      return '16px';
  }
};

const getFontSizing = (sizing: ButtonGroupSizing) => {
  switch (sizing) {
    case 'small':
    case 'medium':
      return 11;
    case 'large':
      return 12;
    default:
      sizing as never;
  }
};

const getPaddingX = (sizing: ButtonGroupSizing, hasLabel: boolean) => {
  if (hasLabel) {
    switch (sizing) {
      case 'small':
      case 'medium':
        return 8;
      case 'large':
        return 12;
      default:
        sizing as never;
    }
  } else {
    switch (sizing) {
      case 'small':
        return '2px';
      case 'medium':
      case 'large':
        return 4;
      default:
        sizing as never;
    }
  }
};

const getPadding = (sizing: ButtonGroupSizing, hasLabel: boolean) => {
  switch (sizing) {
    case 'small':
      return {
        paddingBlock: 2,
        paddingInline: getPaddingX(sizing, hasLabel),
      };
    default:
      return {
        paddingBlock: 4,
        paddingInline: getPaddingX(sizing, hasLabel),
      };
  }
};

const buttonGroupItemStyles = {
  cursor: 'default',
  opacity: 1,
  marginInlineStart: -1,
  position: 'relative',
  width: '100%',
} as const;

const buttonGroupItemVariantStyles = {
  firstChild: {
    marginInlineStart: 0,
  },
  isDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
} as const;

const View = styled('label')(
  buttonGroupItemStyles,
  buttonGroupItemVariantStyles
);

const ItemInput = emotionStyled('input')({
  ...visuallyHiddenStyles,
  display: 'flex',
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
});

// @ts-expect-error - TS7031 - Binding element 'hasLabel' implicitly has an 'any' type. | TS7031 - Binding element 'sizing' implicitly has an 'any' type. | TS7031 - Binding element 'theme' implicitly has an 'any' type. | TS7031 - Binding element 'style' implicitly has an 'any' type.
const buttonGroupButtonStyles = ({hasLabel, sizing, theme, style}) => ({
  ...buttonStyle({theme, style}),
  ...getPadding(sizing, hasLabel),
  appearance: 'none',
  borderTopColor: theme.control.borderColor,
  borderBottomColor: theme.control.borderColor,
  borderRightColor: theme.control.borderColor,
  borderLeftColor: theme.control.borderColor,
  borderStyle: 'solid',
  borderWidth: 1,
  fontSize: getFontSizing(sizing),
  marginInlineStart: -1,
  position: 'relative',
  width: '100%',
});

const buttonGroupButtonVariantStyles = {
  ...buttonVariants,
  // @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
  isChecked: (props) => ({
    ...buttonVariants.selected(props),
    borderTopColor: props.theme.colors.borderDeep,
    borderBottomColor: props.theme.colors.borderDeep,
    borderRightColor: props.theme.colors.borderDeep,
    borderLeftColor: props.theme.colors.borderDeep,
  }),
  firstChild: {
    marginInlineStart: 0,
  },
  // @ts-expect-error - TS7031 - Binding element 'theme' implicitly has an 'any' type.
  focus: ({theme}) => ({
    borderTopColor: theme.colors.accent,
    borderBottomColor: theme.colors.accent,
    borderRightColor: theme.colors.accent,
    borderLeftColor: theme.colors.accent,
    zIndex: 1,
  }),
  // @ts-expect-error - TS7031 - Binding element 'theme' implicitly has an 'any' type.
  keyboardFocus: ({theme}) => ({
    borderTopColor: theme.colors.accent,
    borderBottomColor: theme.colors.accent,
    borderRightColor: theme.colors.accent,
    borderLeftColor: theme.colors.accent,
    zIndex: 1,
  }),
  resetMarginOffset: {
    marginInlineStart: 0,
  },
} as const;

const VisualButton = styled('div')(
  buttonGroupButtonStyles,
  buttonGroupButtonVariantStyles
);
const Button = styled('button')(
  buttonGroupButtonStyles,
  buttonGroupButtonVariantStyles
);

const TextLabel = emotionStyled('span')(
  {whiteSpace: 'nowrap', color: '$textSecondary'},
  {variants: {isActive: {true: {color: '$textPrimary'}}}}
);

export const ButtonGroupItem = React.forwardRef<any, ButtonGroupItemProps>(
  (props, ref) => {
    const {
      buttonProps,
      icon,
      inputProps,
      radiogroup,
      radiogroupProps,
      sizing,
      visualButtonProps,
      ...rest
    } = props;
    const [hitState, hitHandlers] = useHitarea(inputProps);
    const isChecked = rest.isChecked;

    /**
     * Work-around for the aria-label or label Flow type handling.
     */
    // @ts-expect-error - TS2339 - Property 'label' does not exist on type 'PropsWithChildren<ButtonGroupItemProps>'. | TS2339 - Property 'label' does not exist on type 'PropsWithChildren<ButtonGroupItemProps>'.
    const label = props.label ? props.label : null;
    const restProps = omit(rest, ['label']);

    const innerContent = (
      <>
        {icon && (
          <Icon
            glyph={icon}
            height={sizing ? getLineHeight(sizing) : undefined}
            marginRight={label ? 2 : 0}
          />
        )}
        {label && <TextLabel isActive={isChecked}>{label}</TextLabel>}
      </>
    );

    if (radiogroup) {
      return (
        <View
          {...restProps}
          {...radiogroupProps}
          {...hitState}
          {...hitHandlers}
          ref={ref}
        >
          <ItemInput type="radio" {...inputProps} {...hitHandlers} />
          <VisualButton {...visualButtonProps} {...hitState} resetMarginOffset>
            {innerContent}
          </VisualButton>
        </View>
      );
    }

    return (
      <Button
        {...restProps}
        {...buttonProps}
        {...hitState}
        {...hitHandlers}
        ref={ref}
      >
        {innerContent}
      </Button>
    );
  }
);

export default ButtonGroupItem;
